<template>
  <div>
  <div class="flex shadow-sm rounded-md cursor-pointer" role="button" tabindex="0" @click="showModal = true" @keydown.enter="showModal = true" @keydown.space.prevent="showModal = true">
    <div
      class="shrink-0 flex items-center justify-center w-10 md:w-16 text-white text-sm
                                bg-blue-500 leading-5 font-medium rounded-l-md">
      <!-- Icon: Tabler SVG (https://tabler.io/icons) like the rest of the app. -->
      <svg v-if="startingMaintenanceMode === false" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-settings">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
        <path d="M10.325 4.317c.426 -1.756 2.924 -1.756 3.35 0a1.724 1.724 0 0 0 2.573 1.066c1.543 -.94 3.31 .826 2.37 2.37a1.724 1.724 0 0 0 1.065 2.572c1.756 .426 1.756 2.924 0 3.35a1.724 1.724 0 0 0 -1.066 2.573c.94 1.543 -.826 3.31 -2.37 2.37a1.724 1.724 0 0 0 -2.572 1.065c-.426 1.756 -2.924 1.756 -3.35 0a1.724 1.724 0 0 0 -2.573 -1.066c-1.543 .94 -3.31 -.826 -2.37 -2.37a1.724 1.724 0 0 0 -1.065 -2.572c-1.756 -.426 -1.756 -2.924 0 -3.35a1.724 1.724 0 0 0 1.066 -2.573c-.94 -1.543 .826 -3.31 2.37 -2.37c1 .608 2.296 .07 2.572 -1.065z" />
        <path d="M9 12a3 3 0 1 0 6 0a3 3 0 0 0 -6 0" />
      </svg>
      <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-loader-2 animate-spin">
        <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
        <path d="M12 3a9 9 0 1 0 9 9" />
      </svg>
    </div>
    <div class="flex-1 flex items-center justify-between border-t border-r border-b border-gray-200 bg-white rounded-r-md truncate">
      <div class="flex-1 px-4 py-4 text-sm leading-5 truncate">
        <span class="text-gray-900 font-medium hover:text-gray-600">
          Start Maintenance Mode
        </span>
      </div>
      <div class="shrink-0 pr-2">
        <button
          class="w-8 h-8 inline-flex items-center justify-center text-gray-400 rounded-full bg-transparent hover:text-gray-500 focus:outline-none focus:text-gray-500 focus:bg-gray-100">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="icon icon-tabler icons-tabler-outline icon-tabler-chevron-right">
            <path stroke="none" d="M0 0h24v24H0z" fill="none"/>
            <path d="M9 6l6 6l-6 6" />
          </svg>
        </button>
      </div>
    </div>
  </div>

    <app-modal
      v-model:open="showModal"
      title="Start Maintenance Mode"
      primary-label="Start Maintenance Mode"
      :primary-disabled="startDisabled"
      v-on:submit="startMaintenanceMode"
    >
      <p class="text-sm text-gray-700">
        This will start maintenance mode for this point, which will suppress alarms for the specified duration.
      </p>
      <div class="mt-4 text-left">
        <label class="block text-sm font-medium leading-5 text-gray-700 mb-1">Window Name</label>
        <input
          v-model="label"
          type="text"
          placeholder="e.g. Quarterly HVAC maintenance"
          maxlength="100"
          class="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        />
      </div>
      <div class="mt-4 text-left">
        <label class="block text-sm font-medium leading-5 text-gray-700 mb-1">
          Comment<span v-if="requireComment" class="text-red-500 ml-0.5">*</span><span v-else> (optional)</span>
        </label>
        <p v-if="requireComment" class="mb-1 text-base text-gray-500">
          {{ policyText('requireComment') }}
        </p>
        <textarea
          v-model="comment"
          rows="3"
          placeholder="Why is this point going into maintenance?"
          class="block w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
        ></textarea>
      </div>
      <div class="mt-4 text-left">
        <label class="block text-sm font-medium leading-5 text-gray-700 mb-2">Duration</label>
        <div class="grid grid-cols-4 gap-2">
          <button
            v-for="option in durationOptions"
            :key="option.value"
            :class="[
              'px-4 py-2 text-sm font-medium rounded-md border focus:outline-none focus:ring-2 focus:ring-blue-500',
              durationHours === option.value
                ? 'bg-blue-600 text-white border-blue-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            ]"
            type="button"
            @click="durationHours = option.value"
          >
            {{ option.label }}
          </button>
        </div>
      </div>
      <div class="flex items-center mt-4">
        <toggle v-model:value="sendNotification"></toggle>
        <span class="ml-2 text-sm">Send notification to users</span>
      </div>
    </app-modal>
  </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import axios from "axios";
import Toggle from "@/components/elements/toggle";
import AppModal from "@/components/elements/AppModal";
import { alarmPolicyText } from "@/support/alarmPolicyCopy";

export default {
  name: "MeterMaintenanceMode",
  mixins: [SonicuCoreMixin],
  components: {Toggle, AppModal},
  props: {
    meter: {
      type: Object,
      required: true
    },
    shouldNotifyDefault: {
      type: Boolean,
      default: true
    },
    requireComment: {
      type: Boolean,
      default: false,
    },
  },
  data(){
    return {
      showModal: false,
      startingMaintenanceMode: false,
      label: '',
      comment: '',
      durationHours: 4,
      durationOptions: [
        { value: 1, label: '1 hr' },
        { value: 4, label: '4 hrs' },
        { value: 8, label: '8 hrs' },
        { value: 24, label: '24 hrs' },
      ],
      sendNotification: this.shouldNotifyDefault,
    }
  },
  computed: {
    startDisabled() {
      return this.requireComment && this.comment.trim() === '';
    },
  },
  methods: {
    startMaintenanceMode(){
      this.startingMaintenanceMode = true;
      axios.post(this.company.buildCompanyApiInternalRoute('alarms/maintenance/' + this.meter.ID), {
        duration_hours: this.durationHours,
        send_notification: this.sendNotification,
        label: this.label || null,
        comment: this.comment.trim() || null,
      }).then(
        response => {
          // Reload the page or redirect to a success view
          window.location.reload();
        }
      ).catch(
        error => {
          this.startingMaintenanceMode = false;
          // Handle error - you might want to show an error message
          if (error.response && error.response.data && error.response.data.error) {
            alert(error.response.data.error);
          } else {
            alert('An error occurred while starting maintenance mode');
          }
        }
      )
    },
    policyText(key) {
      return alarmPolicyText(key);
    },
  }
}
</script>